import {
  SkyBadge,
  SkyButton,
  SkyMenu,
  SkyText,
  SkyTooltip,
  type SkyMenuContent,
} from "@skypointcloud/skypoint-ui";
import type { ReactNode } from "react";
import { Link } from "wouter";
import type { Reliability, ReliabilityWindow } from "@shared/types/reliability";
import type { ScheduleSummary } from "@shared/types/schedules";
import {
  ScheduleReliabilityLines,
  scheduleReliabilityOf,
} from "@/components/reliability/ScheduleReliability";
import {
  DeleteIcon,
  EditIcon,
  MoreActionsIcon,
  PauseIcon,
  PauseUntilIcon,
  PlayIcon,
  RunsIcon,
  ViewIcon,
} from "@/lib/icons";
import { absolutePath } from "@/lib/routes";
import { timezoneAbbreviation } from "@/lib/timezones";
import { NO_UPCOMING_RUN, formatPauseEnd, isPaused } from "./scheduleForm";
import { COMPLETED_HINT, COMPLETED_LABEL, isCompleted } from "@/lib/schedules/once";
import { formatInstant, instantHoverTitle, zoneLabel } from "@/lib/time/displayTime";

/**
 * #1411: the Schedules list's cell bodies and row menu, moved out of `SchedulesTable` so the
 * component's cognitive complexity stays low. Nothing here changes what a cell shows or does.
 */

/** A clock time as cronstrue writes one: `02:00 AM`, `14:30`. */
const CLOCK_TIME = /\b\d{1,2}:\d{2}(?::\d{2})?(?:\s?[AP]M)?/gi;

/**
 * Walk 2026-10-02, D6 (review r1): the schedule's zone abbreviation follows the last clock time the
 * description names ("At 02:00 AM PDT, only on Monday"); a description with no clock time (an
 * interval, or the raw cron `describeCron` falls back to) carries none, as it names no time to qualify.
 */
export function withScheduleZone(description: string, abbreviation: string): string {
  const times = [...description.matchAll(CLOCK_TIME)];
  const last = times[times.length - 1];
  if (last?.index === undefined) return description;
  const end = last.index + last[0].length;
  return `${description.slice(0, end)} ${abbreviation}${description.slice(end)}`;
}

/**
 * What the State cell says on mouse over and to a screen reader: an enabled schedule's next run in
 * the viewer's zone (as the Next run column shows it), a paused one's end in the same zone, a disabled one's reason.
 */
export function scheduleStateHint(
  schedule: ScheduleSummary,
  zone: string,
  now: Date = new Date(),
): string {
  if (!schedule.enabled) return "Disabled. No import starts until it is enabled.";
  if (isPaused(schedule.pausedUntil, now)) {
    return `Paused until ${formatPauseEnd(schedule.pausedUntil as string, zone)}. No import starts before then. It starts again by itself.`;
  }
  // Round 10 wave 2 (S5): a Once schedule whose dates have all run.
  if (isCompleted(schedule)) return `${COMPLETED_HINT}.`;
  if (!schedule.nextFireAt) return `Enabled. ${NO_UPCOMING_RUN}.`;
  return `Enabled. Next run ${formatInstant(schedule.nextFireAt, zone, "seconds")} (${zoneLabel(zone)}).`;
}

/** The Next run cell: none while disabled or Completed, the next firing in the viewer's zone, or none due. */
export function nextRunText(row: ScheduleSummary, zone: string): string {
  if (!row.enabled) return "Not scheduled while disabled";
  // #1392 item 37: a Once schedule whose dates have all run is done, not skipped past.
  if (isCompleted(row)) return `None, ${COMPLETED_LABEL}`;
  return row.nextFireAt ? formatInstant(row.nextFireAt, zone, "seconds") : NO_UPCOMING_RUN;
}

/** The Runs cell: the description in the schedule's own zone, and the clash warning when it has one. */
export function RunsCell({
  row,
  now,
  clashWith,
}: {
  row: ScheduleSummary;
  now: Date;
  clashWith: string[] | undefined;
}) {
  return (
    <>
      <SkyText size={300} block testId={`schedule-runs-${row.id}`}>
        {row.description
          ? withScheduleZone(row.description, timezoneAbbreviation(row.timezone, now))
          : "No cron expression"}
      </SkyText>
      {clashWith ? (
        <SkyText size={200} color="warning" block testId={`schedule-clash-${row.id}`}>
          {`Same time as ${clashWith.join(", ")} on a shared connection: one of them is skipped.`}
        </SkyText>
      ) : null}
    </>
  );
}

/**
 * The State cell (round 3, owner, 2026-09-30: "the mouse over should show what it is"): a tooltip
 * in every state, on hover and on keyboard focus (the trigger takes focus, as the connections
 * list's Run time does), carrying the same sentence for a screen reader, which never sees a
 * tooltip. The badges are hidden from it: the sentence starts with them. The trigger holds the
 * sentence already, so it drops the `aria-describedby` Radix adds while the tooltip is open;
 * otherwise a screen reader reads the sentence twice (review r1 N2).
 */
export function StateCell({ row, zone, now }: { row: ScheduleSummary; zone: string; now: Date }) {
  const hint = scheduleStateHint(row, zone, now);
  return (
    <SkyTooltip content={hint} testId={`schedule-state-${row.id}-hint`}>
      <span
        tabIndex={0}
        className="block"
        aria-describedby={undefined}
        data-testid={`schedule-state-${row.id}`}
      >
        <span aria-hidden="true" className="flex flex-wrap items-center gap-1">
          <SkyBadge appearance="tint" color={row.enabled ? "success" : "subtle"}>
            {row.enabled ? "Enabled" : "Disabled"}
          </SkyBadge>
          {isCompleted(row) ? (
            <SkyBadge appearance="tint" color="subtle" testId={`schedule-completed-${row.id}`}>
              {COMPLETED_LABEL}
            </SkyBadge>
          ) : null}
          {row.enabled && isPaused(row.pausedUntil, now) ? (
            <SkyBadge
              appearance="tint"
              color="warning"
              testId={`schedule-paused-${row.id}`}
              className="max-w-full"
            >
              {/* A pause that ends mid-day is wider than State: it stops at the cell and
                  cuts, and the tooltip and the sentence carry its whole end (r1 N3). */}
              <span className="min-w-0 truncate">
                {`Paused until ${formatPauseEnd(row.pausedUntil as string, zone)}`}
              </span>
            </SkyBadge>
          ) : null}
        </span>
        <span className="sr-only">{hint}</span>
      </span>
    </SkyTooltip>
  );
}

/** The Next run cell: the viewer's zone, with a hover title only while the schedule is enabled. */
export function NextRunCell({ row, zone }: { row: ScheduleSummary; zone: string }) {
  return (
    <SkyText
      size={300}
      block
      testId={`schedule-next-${row.id}`}
      title={row.enabled ? instantHoverTitle(row.nextFireAt, zone) : undefined}
    >
      {nextRunText(row, zone)}
    </SkyText>
  );
}

export interface LastRunReliability {
  data: Reliability | undefined;
  window: ReliabilityWindow;
}

/**
 * #828: the last run's time (a link to the run), or Never; under it, how the last run ended and
 * the success rate over the chosen window. A column of their own would push the floor past the
 * pane at 1280 px (the layout test).
 */
export function LastRunCell({
  row,
  zone,
  reliability,
}: {
  row: ScheduleSummary;
  zone: string;
  reliability: LastRunReliability | undefined;
}) {
  return (
    <>
      {row.lastRunId ? (
        <Link
          href={absolutePath(`/runs/${row.lastRunId}`)}
          className="underline-offset-2 hover:underline"
        >
          <SkyText
            size={300}
            color="accent"
            title={instantHoverTitle(row.lastRunAt ?? row.lastFiredAt, zone)}
          >
            {formatInstant(row.lastRunAt ?? row.lastFiredAt, zone, "seconds")}
          </SkyText>
        </Link>
      ) : (
        <SkyText size={300} color="muted">
          Never
        </SkyText>
      )}
      {row.lastRunId && row.lastRunManual ? (
        <SkyText size={200} color="muted" block testId={`schedule-last-manual-${row.id}`}>
          Run now
        </SkyText>
      ) : null}
      {reliability ? (
        <ScheduleReliabilityLines
          row={scheduleReliabilityOf(reliability.data, row.id)}
          window={reliability.window}
        />
      ) : null}
    </>
  );
}

export interface ActionsContext {
  canWrite: boolean;
  canDelete: boolean;
  canStartRun: boolean;
  canReadRuns: boolean;
  /** #375, #923: why Run now is off while the destination is not Ready, or null. */
  destinationReason: string | null;
  now: Date;
  navigate: (path: string) => void;
  onRunNow: (schedule: ScheduleSummary) => void;
  onDelete: (schedule: ScheduleSummary) => void;
  onSetEnabled: (schedule: ScheduleSummary, enabled: boolean) => void;
  onResume: (schedule: ScheduleSummary) => void;
  onPause: (schedule: ScheduleSummary) => void;
}

/** Session 10 (P9): only an enabled schedule can be paused; a paused one is resumed. */
function pauseOrResumeItem(row: ScheduleSummary, ctx: ActionsContext): SkyMenuContent | null {
  if (!row.enabled) return null;
  if (isPaused(row.pausedUntil, ctx.now)) {
    return {
      key: "resume",
      label: "Resume schedule",
      icon: <PlayIcon className="size-4" />,
      onClick: () => ctx.onResume(row),
    };
  }
  return {
    key: "pause",
    label: "Pause…",
    icon: <PauseUntilIcon className="size-4" />,
    onClick: () => ctx.onPause(row),
  };
}

/** The `schedules.write` items: Edit, the enable switch, and Pause or Resume. */
function writeItems(row: ScheduleSummary, ctx: ActionsContext): SkyMenuContent[] {
  const items: SkyMenuContent[] = [
    {
      key: "edit",
      label: "Edit schedule",
      icon: <EditIcon className="size-4" />,
      // Routed, not a document load: the dev token lives in
      // sessionStorage and a full navigation re-enters the app before
      // the auth provider has read it.
      onClick: () => ctx.navigate(absolutePath(`/schedules/${row.id}`)),
    },
    // `schedules.write` governs the enable and disable routes too.
    {
      key: "toggle",
      label: row.enabled ? "Disable schedule" : "Enable schedule",
      icon: row.enabled ? <PauseIcon className="size-4" /> : <PlayIcon className="size-4" />,
      onClick: () => ctx.onSetEnabled(row, !row.enabled),
    },
  ];
  const pauseOrResume = pauseOrResumeItem(row, ctx);
  if (pauseOrResume) items.push(pauseOrResume);
  return items;
}

/** The row menu's items, in order: Run now, the write items, Delete. */
export function actionItems(row: ScheduleSummary, ctx: ActionsContext): SkyMenuContent[] {
  const items: SkyMenuContent[] = [];
  if (ctx.canStartRun) {
    items.push({
      key: "run",
      label: "Run now",
      icon: <RunsIcon className="size-4" />,
      // #375, #923: the server refuses every run while the destination is not Ready.
      ...(ctx.destinationReason ? { disabled: true, description: ctx.destinationReason } : {}),
      onClick: () => ctx.onRunNow(row),
    });
  }
  if (ctx.canWrite) items.push(...writeItems(row, ctx));
  if (ctx.canDelete) {
    items.push({
      key: "delete",
      label: "Delete schedule",
      icon: <DeleteIcon className="size-4" />,
      danger: true,
      onClick: () => ctx.onDelete(row),
    });
  }
  return items;
}

/**
 * Round 4 item 6: the schedule's Run history one click from its row, as the connections list has
 * it: an icon-only eye before the menu, hover text through `SkyTooltip`, the accessible name the
 * row's. Every reader of runs gets it, a Viewer too.
 */
function historyButton(row: ScheduleSummary, ctx: ActionsContext): ReactNode {
  if (!ctx.canReadRuns) return null;
  return (
    <SkyTooltip content="View run history">
      <SkyButton
        appearance="subtle"
        size="small"
        icon={ViewIcon}
        aria-label={`View run history for ${row.name}`}
        testId={`schedule-history-${row.id}`}
        onClick={() => ctx.navigate(absolutePath(`/schedules/${row.id}?tab=history`))}
      />
    </SkyTooltip>
  );
}

/** The overflow menu, or null for a Viewer: a button that opens onto nothing reads as broken. */
function actionsMenu(row: ScheduleSummary, items: SkyMenuContent[]): ReactNode {
  if (items.length === 0) return null;
  return (
    <SkyMenu
      align="end"
      items={items}
      trigger={
        <SkyButton
          appearance="subtle"
          size="small"
          icon={MoreActionsIcon}
          aria-label={`Actions for ${row.name}`}
          testId={`schedule-actions-${row.id}`}
        />
      }
    />
  );
}

/** The Actions cell: the history eye beside the menu, or nothing when the reader has neither. */
export function ActionsCell({ row, ctx }: { row: ScheduleSummary; ctx: ActionsContext }) {
  const history = historyButton(row, ctx);
  const menu = actionsMenu(row, actionItems(row, ctx));
  if (!history && !menu) return null;
  return (
    <div className="flex items-center justify-end gap-1">
      {history}
      {menu}
    </div>
  );
}
